import axiosLib from 'axios';

const axios = axiosLib.create({
  maxRedirects: 0,
});

const api = {
  async login(email, password) {
    const response = await axios.put(
      `${process.env.VUE_APP_ACCOUNTS_API}/authenticate/login-sunset-admin`,
      {
        credentials: {
          user: email,
          pass: password,
        },
      },
      {
        withCredentials: true,
      }
    );

    if (response.status >= 200 && response.status < 300) {
      const token = response.data.accountsJWT || response.data.access_token || response.data.token;
      if (token) {
        return { success: true, token };
      }
      throw new Error('Invalid response from authentication service: No token received');
    }
    throw new Error(`Unexpected response status: ${response.status}`);
  },

  buildQueryForExport(endpointId, fieldData) {
    if (endpointId === 'export-contacts') {
      return {};
    }
    
    return {};
  },

  buildCustomColumns(allColumns, fieldData) {
    if (allColumns) {
      return [];
    }
    return [];
  },

  buildColumnsOrder(allColumns, fieldData, customColumns = []) {
    if (!allColumns && customColumns.length > 0) {
      const order = {};
      customColumns.forEach((col, index) => {
        order[index] = col;
      });
      return order;
    }
    
    return { 0: 'id' };
  },


  async callEndpoint(endpoint, data = {}, authToken) {
    console.log('[SunsetAdmin API] callEndpoint called', { 
      endpointId: endpoint.id, 
      endpointUrl: endpoint.url,
      method: endpoint.method,
      data,
      hasAuthToken: !!authToken 
    });

    if (!authToken) {
      console.error('[SunsetAdmin API] No auth token provided');
      throw new Error('Not authenticated');
    }

    let finalData = {};

    if (endpoint.id === 'export-contacts') {
      console.log('[SunsetAdmin API] Building export-contacts payload', { data });
      const allColumns = true; 
      const query = this.buildQueryForExport(endpoint.id, data);
      const customColumns = this.buildCustomColumns(allColumns, data);
      const columnsOrder = this.buildColumnsOrder(allColumns, data, customColumns);
      
      console.log('[SunsetAdmin API] Export payload components', {
        query,
        customColumns,
        columnsOrder,
        allColumns,
      });
      
      finalData = {
        sunset_admin_request: true,
        query: query,
        file_type: data.file_type,
        all_columns: allColumns,
        is_private: false,
        is_static_list: false,
        columns_order: columnsOrder,
        use_database: true,
      };
      
      if (data.user_id) {
        finalData.user_id = data.user_id;
      }
      
      if (!allColumns && customColumns.length > 0) {
        finalData.custom_columns = customColumns;
      }
      
      if (!finalData.query.$skip) {
        finalData.query.$skip = 0;
      }
      
      console.log('[SunsetAdmin API] Final export-contacts payload', finalData);
    } else if (endpoint.id === 'export-events') {
      const allColumns = true; 
      const customColumns = this.buildCustomColumns(allColumns, data);
      const columnsOrder = this.buildColumnsOrder(allColumns, data, customColumns);
      
      finalData = {
        sunset_admin_request: true,
        file_type: data.file_type,
        all_columns: allColumns,
        is_private: false,
        columns_order: columnsOrder,
        user_id: data.user_id,
        use_database: true,
        // Hardcode all optional fields to default values
        contact_id: 0,
        current_user_only: false,
        teams: false,
        pinned_only: false,
      };
    } else if (endpoint.id === 'export-templates') {
      finalData = {
        sunset_admin_request: true,
        user_id: data.user_id,
      };
    } else if (endpoint.id === 'export-documents') {
      finalData = {
        sunset_admin_request: true,
        user_id: data.user_id,
      };
    }

    const endpointConfig = {
      'export-contacts': {
        envVar: 'VUE_APP_CONTACTS_MS_BASE_URL',
        value: process.env.VUE_APP_CONTACTS_MS_BASE_URL,
      },
      'export-events': {
        envVar: 'VUE_APP_EVENTS_API_BASE_URL',
        value: process.env.VUE_APP_EVENTS_API_BASE_URL,
      },
      'export-templates': {
        envVar: 'VUE_APP_DOCUMENTS_API_BASE_URL',
        value: process.env.VUE_APP_DOCUMENTS_API_BASE_URL,
      },
      'export-documents': {
        envVar: 'VUE_APP_DOCUMENTS_API_BASE_URL',
        value: process.env.VUE_APP_DOCUMENTS_API_BASE_URL,
      },
    };

    const endpointConf = endpointConfig[endpoint.id];
    if (!endpointConf) {
      throw new Error(`Unknown endpoint: ${endpoint.id}`);
    }
    if (!endpointConf.value) {
      throw new Error(`${endpointConf.envVar} is not configured`);
    }

    const baseURL = endpointConf.value;
    let url = `${baseURL}${endpoint.url}`.replace(/([^:]\/)\/+/g, '$1');
    
    // For template export, add type as query parameter
    if (endpoint.id === 'export-templates') {
      url = `${url}?type=templates`;
    } else if (endpoint.id === 'export-documents') {
      url = `${url}?type=documents`;
    }
    
    console.log('[SunsetAdmin API] Request configuration', {
      method: endpoint.method.toLowerCase(),
      url,
      baseURL,
      endpointUrl: endpoint.url,
      hasData: Object.keys(finalData).length > 0,
    });
    
    const config = {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'x-liondesk-dev': 'true',
      },
    };

    console.log('[SunsetAdmin API] Request headers', {
      'Content-Type': config.headers['Content-Type'],
      'x-liondesk-dev': config.headers['x-liondesk-dev'],
      'Authorization': config.headers['Authorization'] ? 'Bearer [REDACTED]' : 'missing',
    });

    console.log('[SunsetAdmin API] Sending request', {
      method: endpoint.method.toLowerCase(),
      url,
      payload: Object.keys(finalData).length > 0 ? finalData : undefined,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await axios({
        method: endpoint.method.toLowerCase(),
        url,
        data: Object.keys(finalData).length > 0 ? finalData : undefined,
        ...config,
      });

      console.log('[SunsetAdmin API] Request successful', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers,
      });

      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error('[SunsetAdmin API] Request failed', {
        error: error.message,
        status: error.response && error.response.status,
        statusText: error.response && error.response.statusText,
        data: error.response && error.response.data,
        url: error.config && error.config.url,
        method: error.config && error.config.method,
        requestHeaders: error.config && error.config.headers,
        responseHeaders: error.response && error.response.headers,
        stack: error.stack,
      });

      if (error && error.response && error.response.status === 401) {
        console.error('[SunsetAdmin API] Authentication failed - session expired');
        throw new Error('Session expired. Please login again.');
      }
      
      const errorResponse = {
        status: (error && error.response && error.response.status) || 500,
        data: (error && error.response && error.response.data) || { error: (error && error.message) || 'Unknown error' },
      };
      
      console.error('[SunsetAdmin API] Throwing error response', errorResponse);
      throw errorResponse;
    }
  },

  async getUserData(email, authToken) {
    
    if (!authToken) {
      throw new Error('Not authenticated');
    }

    const baseUrl = process.env.VUE_APP_ACCOUNTS_API;
    const url = `${baseUrl}/users/data`;
    
    const config = {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      params: {
        email: email,
      },
    };

    try {
      const response = await axios.get(url, config);
      
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      
      if (error && error.response) {
        const status = error.response.status;
        
        if (status === 401 || status === 403) {
          throw new Error('Session expired. Please login again.');
        }
        
        throw {
          status: status,
          message: error.message || 'Request failed',
          data: error.response.data || { error: error.message || 'Request failed' },
        };
      }
      
      if (error.status) {
        throw error;
      }
      
      throw {
        status: 500,
        message: error.message || 'Network error',
        data: { error: error.message || 'Network error' },
      };
    }
  },

  async getUserById(userId, authToken) {
    if (!authToken) {
      throw new Error('Not authenticated');
    }

    const baseUrl = process.env.VUE_APP_USERS_MS_BASE_URL || process.env.VUE_APP_ACCOUNTS_API;
    // Remove trailing slash if present
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    // VUE_APP_ACCOUNTS_API already includes /api/v1, so only append /api/v1 if using Users MS
    const pathPrefix = baseUrl === process.env.VUE_APP_ACCOUNTS_API ? '' : '/api/v1';
    const url = `${cleanBaseUrl}${pathPrefix}/users/${userId}?basic=true`;
    
    const config = {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    };

    try {
      const response = await axios.get(url, config);
      
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      if (error && error.response) {
        const status = error.response.status;
        
        if (status === 401 || status === 403) {
          throw new Error('Session expired. Please login again.');
        }
        
        throw {
          status: status,
          message: error.message || 'Request failed',
          data: error.response.data || { error: error.message || 'Request failed' },
        };
      }
      
      if (error.status) {
        throw error;
      }
      
      throw {
        status: 500,
        message: error.message || 'Network error',
        data: { error: error.message || 'Network error' },
      };
    }
  },

  async getUserMigrationData(userId, authToken) {
    if (!authToken) {
      throw new Error('Not authenticated');
    }

    const baseUrl = process.env.VUE_APP_ACCOUNTS_API;
    const url = `${baseUrl}/users/migration-data`;
    
    const config = {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      params: {
        user_id: userId,
      },
    };

    try {
      const response = await axios.get(url, config);
      
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      if (error && error.response) {
        const status = error.response.status;
        
        if (status === 401 || status === 403) {
          throw new Error('Session expired. Please login again.');
        }
        
        throw {
          status: status,
          message: error.message || 'Request failed',
          data: error.response.data || { error: error.message || 'Request failed' },
        };
      }
      
      if (error.status) {
        throw error;
      }
      
      throw {
        status: 500,
        message: error.message || 'Network error',
        data: { error: error.message || 'Network error' },
      };
    }
  },

  async getUsers(authToken) {
    if (!authToken) {
      throw new Error('Not authenticated');
    }

    const baseUrl = process.env.VUE_APP_USERS_MS_BASE_URL || process.env.VUE_APP_ACCOUNTS_API;
    // Remove trailing slash if present
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    // VUE_APP_ACCOUNTS_API already includes /api/v1, so only append /api/v1 if using Users MS
    const pathPrefix = baseUrl === process.env.VUE_APP_ACCOUNTS_API ? '' : '/api/v1';
    const url = `${cleanBaseUrl}${pathPrefix}/users`;
    
    const config = {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    };

    try {
      const response = await axios.get(url, config);
      
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      if (error && error.response) {
        const status = error.response.status;
        
        if (status === 401 || status === 403) {
          throw new Error('Session expired. Please login again.');
        }
        
        throw {
          status: status,
          message: error.message || 'Request failed',
          data: error.response.data || { error: error.message || 'Request failed' },
        };
      }
      
      if (error.status) {
        throw error;
      }
      
      throw {
        status: 500,
        message: error.message || 'Network error',
        data: { error: error.message || 'Network error' },
      };
    }
  },

  async loginAsUser(userId, authToken) {
    if (!authToken) {
      throw new Error('Not authenticated');
    }

    // Step 1: Get rl6Token from Users MS
    const baseUrl = process.env.VUE_APP_USERS_MS_BASE_URL || process.env.VUE_APP_ACCOUNTS_API;
    // Remove trailing slash if present
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    // VUE_APP_ACCOUNTS_API already includes /api/v1, so only append /api/v1 if using Users MS
    const pathPrefix = baseUrl === process.env.VUE_APP_ACCOUNTS_API ? '' : '/api/v1';
    const usersMSUrl = `${cleanBaseUrl}${pathPrefix}/users/${userId}/impersonate-tokens`;
    
    let rl6Token;
    try {
      const usersMSResponse = await axios.post(usersMSUrl, {}, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!usersMSResponse.data || !usersMSResponse.data.rl6Token) {
        throw new Error('Failed to get RL6 token from Users MS');
      }
      
      rl6Token = usersMSResponse.data.rl6Token;
    } catch (error) {
      if (error && error.response) {
        const status = error.response.status;
        
        if (status === 401 || status === 403) {
          throw new Error('Session expired. Please login again.');
        }
        
        throw {
          status: status,
          message: error.message || 'Request failed',
          data: error.response.data || { error: error.message || 'Request failed' },
        };
      }
      
      if (error.status) {
        throw error;
      }
      
      throw {
        status: 500,
        message: error.message || 'Network error',
        data: { error: error.message || 'Network error' },
      };
    }

    // Step 2: Call Accounts API /by-token-for-impersonation (withCredentials: true to receive cookies)
    // Accounts API will set the refreshToken cookie (httpOnly) automatically
    const accountsAPIUrl = `${process.env.VUE_APP_ACCOUNTS_API}/authenticate/by-token-for-impersonation`;
    
    try {
      const accountsResponse = await axios.put(accountsAPIUrl, {
        credentials: {
          ldToken: rl6Token,
        },
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Origin': 'liondesk.com',
          'Cache-Control': 'no-cache',
          'x-device': 'browser',
          'x-rl6-validate': 'true',
        },
        withCredentials: true, // Important: allows browser to receive Set-Cookie header
      });
      
      return {
        status: accountsResponse.status,
        data: accountsResponse.data,
        success: true,
      };
    } catch (error) {
      if (error && error.response) {
        const status = error.response.status;
        
        if (status === 401 || status === 403) {
          throw new Error('Failed to authenticate with Accounts API');
        }
        
        throw {
          status: status,
          message: error.message || 'Request failed',
          data: error.response.data || { error: error.message || 'Request failed' },
        };
      }
      
      if (error.status) {
        throw error;
      }
      
      throw {
        status: 500,
        message: error.message || 'Network error',
        data: { error: error.message || 'Network error' },
      };
    }
  },
};

export default api;

